import type { RawCreateParams, ZodTypeAny } from 'zod';
import { z } from 'zod';

import type { CONTINENT, COUNTRY_GEODATA, COUNTRY_ISO3166_ALPHA3, US_STATES } from '@endaoment-frontend/constants';

type Branded<T, U> = T & { __brand: U };
export type Letter =
  | 'A'
  | 'B'
  | 'C'
  | 'D'
  | 'E'
  | 'F'
  | 'G'
  | 'H'
  | 'I'
  | 'J'
  | 'K'
  | 'L'
  | 'M'
  | 'N'
  | 'O'
  | 'P'
  | 'Q'
  | 'R'
  | 'S'
  | 'T'
  | 'U'
  | 'V'
  | 'W'
  | 'X'
  | 'Y'
  | 'Z';
export type UUID = Branded<string, 'UUID'>;
const GUID_REGEX = /^[\da-fA-F]{8}\b-[\da-fA-F]{4}\b-[\da-fA-F]{4}\b-[\da-fA-F]{4}\b-[\da-fA-F]{12}$/;
export const uuidSchema = z.custom<UUID>(
  v => {
    if (typeof v !== 'string') return false;
    return GUID_REGEX.test(v);
  },
  {
    message: `Invalid UUID`,
  },
);
export const isUuid = (v: string): v is UUID => uuidSchema.safeParse(v).success;

export const percentageNumberSchema = z.number().min(0).max(1).describe('A percentage number between 0 and 1');
export type EIN = Branded<string, 'EIN'>;
export const einSchema = z
  .custom<EIN>(v => z.string().min(9).max(9).safeParse(v).success, {
    message: `Invalid EIN`,
  })
  .describe(`A string composed of 9 digits - Use this type to denote the "clean", dash-less EIN`);

export type EINDash = Branded<string, 'EINDash'>;
export const einDashSchema = z
  .custom<EINDash>(v => {
    if (typeof v !== 'string') return false;
    return /^\d{2}-\d{7}$/.test(v);
  })
  .describe(
    `A string composed of 9 digits plus a single digit, separating the first 2 digits from the last 7 - Use this type to denote the formatted EIN`,
  );

export const timestampSchema = z.union([z.number(), z.string().transform(v => new Date(v).valueOf())]);

export type AdminAccountName = 'accountant' | 'keeper' | 'maintainer' | 'reviewer';

export type USStateCode = z.infer<typeof US_STATES>;

export type CountryCode = z.infer<typeof COUNTRY_ISO3166_ALPHA3>;
export type CountryEntry = (typeof COUNTRY_GEODATA)[CountryCode];

export type ContinentEntry = (typeof CONTINENT)[keyof typeof CONTINENT];
export type ContinentName = (typeof CONTINENT)[keyof typeof CONTINENT]['name'];

export const arraySchemaInvalidsFiltered = <T extends ZodTypeAny>(schema: T, params?: RawCreateParams) => {
  const catchValue = {} as never;
  return z.array(schema.catch(catchValue), params).transform(arr => arr.filter(v => v !== catchValue));
};

export type MetaData = {
  title: string;
  description: string;
  url: string;
  header?: string;
  keywords?: string;
  image?: string;
};
